import { createStore } from "solid-js/store";

export type ReaderFontSize = "small" | "medium" | "large";

interface ReaderState {
  selectedVerses: Record<number, boolean>;
  fontSize: ReaderFontSize;
  showStrongs: boolean;
}

const PREFS_KEY = "bible-reader-prefs";

function loadPrefs(): Pick<ReaderState, "fontSize" | "showStrongs"> {
  const defaults: Pick<ReaderState, "fontSize" | "showStrongs"> = {
    fontSize: "medium",
    showStrongs: true,
  };

  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) {
      return defaults;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return defaults;
    }
    const prefs = parsed as Partial<ReaderState>;
    return {
      fontSize:
        prefs.fontSize === "small" || prefs.fontSize === "medium" || prefs.fontSize === "large"
          ? prefs.fontSize
          : defaults.fontSize,
      showStrongs:
        typeof prefs.showStrongs === "boolean" ? prefs.showStrongs : defaults.showStrongs,
    };
  } catch {
    return defaults;
  }
}

const [state, setState] = createStore<ReaderState>({
  selectedVerses: {},
  ...loadPrefs(),
});

function persistPrefs() {
  try {
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({ fontSize: state.fontSize, showStrongs: state.showStrongs }),
    );
  } catch {
    // localStorage unavailable (private mode / quota): preferences stay in-memory only
  }
}

export const readerStore = {
  get selectedVerses() {
    return state.selectedVerses;
  },

  get fontSize() {
    return state.fontSize;
  },

  get showStrongs() {
    return state.showStrongs;
  },

  toggleVerse(verseNumber: number) {
    setState("selectedVerses", verseNumber, (selected) => !selected);
  },

  clearSelection() {
    setState({ selectedVerses: {} });
  },

  /**
   * Reemplaza la selección por el conjunto exacto dado. Lo usa el sync URL→store
   * (`?sel=`) para reproducir una selección compartida sin acumular toggles.
   */
  setSelection(verseNumbers: number[]) {
    const next: Record<number, boolean> = {};
    for (const v of verseNumbers) {
      if (Number.isInteger(v) && v > 0) {
        next[v] = true;
      }
    }
    setState({ selectedVerses: next });
  },

  // Ciclo small → medium → large → small (paridad con el FontSizeToggleButton legacy)
  cycleFontSize() {
    const next: Record<ReaderFontSize, ReaderFontSize> = {
      small: "medium",
      medium: "large",
      large: "small",
    };
    setState("fontSize", (size) => next[size]);
    persistPrefs();
  },

  /** Fija el tamaño de fuente (lo usa el sync URL→store de `?fs=`). */
  setFontSize(size: ReaderFontSize) {
    setState("fontSize", size);
    persistPrefs();
  },

  toggleStrongs() {
    setState("showStrongs", (show) => !show);
    persistPrefs();
  },

  /** Fija la visibilidad de números Strong (lo usa el sync URL→store de `?strongs=`). */
  setShowStrongs(show: boolean) {
    setState("showStrongs", show);
    persistPrefs();
  },

  getSelectedVersesArray(): number[] {
    return Object.entries(state.selectedVerses)
      .filter((entry) => entry[1])
      .map(([verse]) => Number.parseInt(verse, 10))
      .toSorted((a, b) => a - b);
  },
};
